import { Palette, Info } from "lucide-react";

export const COLOR_CHOICES = [
  { value: 4, label: "4 · duck only" },
  { value: 8, label: "8 · 1× AMS" },
  { value: 12, label: "12 · AMS + 4" },
  { value: 16, label: "16 · 2× AMS" },
];
const PICKER_MAX = 16; // the UI stops here; a saved 20/24 (owner/staff) shows as one extra chip
const NOTE_ABOVE = 8;

// A saved count above the picker's range stays visible (and selectable) as an extra chip.
export const slotChoices = (saved) =>
  saved > PICKER_MAX && !COLOR_CHOICES.some((c) => c.value === saved)
    ? [...COLOR_CHOICES, { value: saved, label: `${saved} · saved` }]
    : COLOR_CHOICES;

// Never blocks. Above 8 spools: the printer capacity text when a capacity is set (only when it does not fit),
// otherwise the generic second-AMS text.
export const slotNote = (slots, needed, capacity) => {
  const n = needed > 0 ? Math.min(slots, needed) : slots;
  if (!(n > NOTE_ABOVE) || (capacity && n <= capacity)) return null;
  return capacity
    ? `Needs ${n} spools; your printers have ${capacity} slots, so filaments would have to be swapped by hand during the print, with a longer print and a bigger prime tower. You can still choose it.`
    : "More than 8 slots needs a second AMS (or swapping spools by hand), a longer print and a bigger prime tower. You can still choose it.";
};

export const SlotCapacityNote = ({ slots, needed, capacity, testid = "slot-capacity-note" }) => {
  const text = slotNote(slots, needed, capacity);
  if (!text) return null;
  return (
    <p data-testid={testid} className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-400 mt-2">
      <Info className="h-3.5 w-3.5 shrink-0 mt-px" /> {text}
    </p>
  );
};

export const ColorCountPicker = ({ value, onChange, needed, capacity, title = "Filament slots in the .3MF" }) => {
  const choices = slotChoices(value);
  const fit = needed > 0 ? choices.find((x) => x.value >= needed)?.value : null;
  return (
    <div data-testid="color-count-picker" className="rounded-3xl border border-border bg-card/60 p-3">
      <p className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-muted-foreground mb-2">
        <Palette className="h-4 w-4" /> {title}
      </p>
      <div className={`grid grid-cols-2 ${choices.length > 4 ? "sm:grid-cols-5" : "sm:grid-cols-4"} gap-2`}>
        {choices.map((c) => (
          <button
            key={c.value}
            type="button"
            data-testid={`color-count-${c.value}`}
            aria-pressed={value === c.value}
            onClick={() => onChange(c.value)}
            className={`rounded-full px-3 py-2 text-xs font-semibold border transition-colors ${
              value === c.value ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"
            }`}
          >
            {c.label}
            {c.value === fit ? " ✓" : ""}
          </button>
        ))}
      </div>
      <SlotCapacityNote slots={value} needed={needed} capacity={capacity} />
      <p className="text-[11px] text-muted-foreground mt-2">
        Yellow body, orange beak, black eyes and white are always reserved; the rest go to outfit details (eyes, buttons, logos get their own spool when distinct).
      </p>
    </div>
  );
};
